import { toast } from "react-toastify";
import i18n from "../../../i18n";
import { isDisplayUnitsExtended } from "../../../global/displayUnitsFlag";

// A6 is not a valid CSS @page size keyword; fall back to inch dimensions.
export const getPageSizeCss = ({ orientation = "portrait", paperFormat = "A4" } = {}) =>
  paperFormat === "A6"
    ? orientation === "landscape"
      ? "5.81in 4.11in"
      : "4.11in 5.81in"
    : `${paperFormat} ${orientation}`;

export const EXPORT_ERROR_TOAST_ID = "export-error";
export const EXPORT_INFO_TOAST_ID = "export-info";
export const toastExportError = (message) => toast.error(message, { toastId: EXPORT_ERROR_TOAST_ID });
export const toastExportInfo = (message) => toast.info(message, { toastId: EXPORT_INFO_TOAST_ID });

// Shared print/PDF-export listeners, spread into the `listeners` of every
// Bryntum entry that prints or exports (project, Program, Task Focus,
// Multi-project and Multi-selected Gantts, the schedulers and the Workload
// grid):
//   1. Browser print never sets @page CSS, so the print preview stays on
//      its default portrait/A4 regardless of the dialog's chosen
//      orientation/paper format — beforeShowPrintDialog injects it into
//      the print iframe.
//   2. Server PDF export comes out portrait regardless of the orientation
//      sent: node_modules/bootstrap ships `@page { size: a3; }` (no
//      orientation keyword), which Bryntum's exporter copies into the
//      exported fragment, and Chromium's print-to-PDF pipeline honors that
//      over the landscape/format options passed to Puppeteer.
//      beforePdfExport appends our own @page rule last in <head> (cascade
//      order wins); pdfExport removes it once the export finishes.
//   3. Bryntum re-renders the live rows while `isExporting` is still true
//      (Exporter#export restores the component before PdfExport#export
//      clears the flag), so export-safe columns keep their text twins:
//      pdfExport repaints the rows once the flag is down.
// Print extends PrintMixin(PdfExport), so beforePdfExport/pdfExport fire for
// the browser-print path too; the print iframe gets its own @page through
// beforeShowPrintDialog.
//
// These MUST be handed to the widget through its `listeners` config, NOT as
// `on<Event>` React props: the Bryntum React wrapper only turns a prop into a
// listener when its name is in the component's own config/property/feature
// name lists (createConfig() in
// @bryntum/gantt-react-thin/lib/WrapperHelper.js). BryntumGantt declares
// onBeforePdfExport and onPdfExport — but NOT onBeforeShowPrintDialog, so that
// prop was silently dropped and the browser print preview never got its @page
// rule. `listeners` is passed through verbatim, hence the un-prefixed Bryntum
// event names below.
// The `#` (sequence) column is what the dependency columns reference, so the
// printed set always carries it, even when it is hidden on screen (SPEC-0006
// §4.7). The exporter shows exactly the ids in `config.columns` while it runs.
const isSequenceColumn = (column) =>
  column?.id === "sequence" || column?.type === "sequence" || column?.data?.type === "sequence";

export const printColumnsWithSequence = (columnIds, columns) => {
  const sequence = (columns?.allRecords || []).find(isSequenceColumn);
  if (!sequence || !Array.isArray(columnIds) || columnIds.includes(sequence.id)) return columnIds;
  return [sequence.id, ...columnIds];
};

// A `beforePdfExport` listener: Print extends PdfExport, so it covers both.
export const addSequenceColumnToPrint = ({ source, config }) => {
  if (!isDisplayUnitsExtended() || !config) return;
  config.columns = printColumnsWithSequence(config.columns, source?.columns);
};

// Bryntum scales every exported page with `transform: scale()` and enlarges its box by the inverse
// ratio (e.g. 134% x 134%), so the layout overflow is bigger than the sheet and Chrome prints a
// blank trailing page for each fragment. Painting is clipped to the page box so nothing overflows.
export const EXPORT_PAGE_CLIP_CSS = "html.b-print-root body.b-export { contain: paint; }";

export const createPrintExportListeners = () => ({
  beforePdfExport: ({ source, config }) => {
    addSequenceColumnToPrint({ source, config });
    source.$printPageSetup = {
      orientation: config.orientation,
      paperFormat: config.paperFormat,
    };
    const style = document.createElement("style");
    style.id = "pdf-export-page-override";
    style.textContent = `@page { size: ${getPageSizeCss(config)}; margin: 0; }\n${EXPORT_PAGE_CLIP_CSS}`;
    document.head.appendChild(style);
  },
  pdfExport: ({ source }) => {
    document.getElementById("pdf-export-page-override")?.remove();
    setTimeout(() => {
      if (!source.isDestroyed) source.refreshRows();
    });
  },
  beforeShowPrintDialog: ({ source, iframe }) => {
    const doc = iframe.contentDocument;
    const style = doc.createElement("style");
    style.textContent = `@page { size: ${getPageSizeCss(
      source.$printPageSetup
    )}; margin: 0; }`;
    doc.head.appendChild(style);
  },
});

export const PRINT_DIALOG_TIMEOUT_MS = 120000;

// Bryntum masks the Gantt while it prepares the print pages; a rejected or
// never-settling promise would leave it masked for good.
export const showPrintDialogSafely = (
  gantt,
  { timeoutMs = PRINT_DIALOG_TIMEOUT_MS } = {}
) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("print timeout")), timeoutMs);
  });
  return Promise.race([
    Promise.resolve().then(() => gantt.features.print.showPrintDialog()),
    timeout,
  ])
    .catch(() => {
      toastExportError(i18n.t("gantt.export.printFailed"));
    })
    .finally(() => {
      clearTimeout(timer);
      gantt.unmask?.();
    });
};

// Runs a Bryntum PDF export action so it can never fail silently or hang:
// timeout (a "TimeoutError"), error toast, and `onSettled` always run. The
// timeout only stops the wait: the caller must stop the export itself.
export const runPdfExportSafely = async (
  action,
  { onSettled, onError, timeoutMs = PRINT_DIALOG_TIMEOUT_MS } = {}
) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(Object.assign(new Error("pdf export timeout"), { name: "TimeoutError" })), timeoutMs);
  });
  try {
    const result = await Promise.race([Promise.resolve().then(action), timeout]);
    // Bryntum's PdfExport.export() resolves on an HTTP error with
    // { response: { ok: false } } (or { error }) instead of rejecting.
    if (result?.error || (result?.response && !result.response.ok)) {
      throw (
        result.error ||
        Object.assign(new Error(`pdf export server answered ${result.response.status}`), {
          status: result.response.status,
        })
      );
    }
  } catch (e) {
    if (!(onError && onError(e))) toastExportError(i18n.t("taskFocus.exportToPdfFailed"));
  } finally {
    clearTimeout(timer);
    try {
      onSettled?.();
    } catch (e) {
      // the view could not be restored; the toast above already informed the user
    }
  }
};
